/**
 * Regroupement des entités du catalogue pour la section « Applications » du menu.
 *
 * Logique pure (sans React) : un groupe par système (`spec.system`), avec ses
 * composants et ressources, et les ressources de type `partner` (marques en
 * marque blanche) mises à part.
 */

export const OTHER_KEY = '__autres__';

export type EntityKind = 'Component' | 'Resource';

export interface CatalogItem {
  kind?: string;
  metadata: { name: string; namespace?: string; title?: string };
  spec?: unknown;
}

export interface AppEntity {
  name: string;
  title?: string;
  namespace: string;
  kind: EntityKind;
  type: string;
  system: string;
}

export interface AppGroup {
  key: string;
  label: string;
  namespace: string;
  services: AppEntity[];
  partners: AppEntity[];
}

/** `system:default/x`, `default/x` ou `x` → `x`. */
export function systemKey(system: unknown): string {
  const raw = typeof system === 'string' ? system : '';
  return raw.replace(/^system:/, '').replace(/^default\//, '');
}

function toEntity(item: CatalogItem): AppEntity {
  const spec = (item.spec ?? {}) as { type?: string; system?: string };
  return {
    name: item.metadata.name,
    title: item.metadata.title,
    namespace: item.metadata.namespace || 'default',
    kind: item.kind === 'Resource' ? 'Resource' : 'Component',
    type: (spec.type ?? '').toLowerCase(),
    system: systemKey(spec.system),
  };
}

function humanize(name: string): string {
  const spaced = name.replace(/[-_]+/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

const byName = (a: AppEntity, b: AppEntity) => a.name.localeCompare(b.name);

export function groupApplications(
  systems: CatalogItem[],
  entities: CatalogItem[],
): AppGroup[] {
  const groups = new Map<string, AppGroup>();
  const ensure = (key: string, label: string, namespace = 'default') => {
    if (!groups.has(key)) groups.set(key, { key, label, namespace, services: [], partners: [] });
    return groups.get(key)!;
  };

  for (const s of systems) {
    ensure(s.metadata.name, s.metadata.title ?? humanize(s.metadata.name), s.metadata.namespace || 'default');
  }
  for (const entity of entities.map(toEntity)) {
    const key = entity.system || OTHER_KEY;
    const group = ensure(key, key === OTHER_KEY ? 'Autres' : humanize(key));
    (entity.kind === 'Resource' && entity.type === 'partner' ? group.partners : group.services).push(entity);
  }

  const result = [...groups.values()].filter(g => g.key !== OTHER_KEY);
  result.sort((a, b) => a.label.localeCompare(b.label));
  for (const g of result) {
    g.services.sort(byName);
    g.partners.sort(byName);
  }
  const other = groups.get(OTHER_KEY);
  if (other && other.services.length + other.partners.length > 0) {
    other.services.sort(byName);
    other.partners.sort(byName);
    result.push(other);
  }
  return result;
}

/** Pastille courte affichée à droite du nom. */
export function typeBadge(entity: Pick<AppEntity, 'kind' | 'type'>): string {
  if (entity.kind === 'Resource') {
    switch (entity.type) {
      case 'database':
        return 'bdd';
      case 'cache':
        return 'cache';
      case 'partner':
        return 'partenaire';
      default:
        return 'res';
    }
  }
  switch (entity.type) {
    case 'website':
      return 'web';
    case 'batch':
      return 'cron';
    case 'library':
      return 'lib';
    default:
      return 'svc';
  }
}
